/**
 * Modal History Manager
 * Manages browser history for modals to enable proper back button navigation.
 *
 * Each pushModal() call returns a unique instanceId so that multiple copies of
 * the same modal type (e.g. two 'single-post' modals) are tracked independently.
 * Pass that instanceId to popModal() instead of the plain type string.
 */

// Stack entries: { type: string, instanceId: string }
let modalStack = [];

// Monotonically increasing counter – gives each push a unique id
let instanceCounter = 0;

// Flag to track if we're currently handling a back navigation
let isHandlingBack = false;

// Track the current page path before opening modals
let savedPagePath = null;

/**
 * Push a modal to the history stack.
 *
 * @param {string} modalType - Human-readable type label ('comments', 'replies', 'single-post', …)
 * @returns {string} instanceId – store this in a ref and pass it to popModal()
 */
export const pushModal = (modalType) => {
  // Save current path if this is the first modal
  if (modalStack.length === 0) {
    savedPagePath = window.location.pathname;
    console.log('💾 Saved page path:', savedPagePath);
  }

  const instanceId = `${modalType}__${++instanceCounter}`;

  modalStack.push({ type: modalType, instanceId });

  window.history.pushState(
    {
      modal: modalType,
      instanceId,
      stackIndex: modalStack.length,
      savedPath: savedPagePath,
    },
    ''
  );

  console.log('Modal pushed:', instanceId, 'Stack:', modalStack.map(m => m.instanceId));
  return instanceId;
};

/**
 * Pop a modal from the stack.
 *
 * @param {string} instanceId   - The value returned by pushModal()
 *                                 (falls back to treating the arg as a type for
 *                                  backwards-compat with any call sites not yet migrated)
 * @param {boolean} popBrowserHistory - true  → modal was closed via UI (call history.back())
 *                                      false → modal was closed by the back button already
 */
export const popModal = (instanceId, popBrowserHistory = false) => {
  // Support legacy callers that still pass a plain type string
  let index = modalStack.findIndex(m => m.instanceId === instanceId);
  if (index === -1) {
    // fallback: treat as type, pop the LAST entry of that type
    index = modalStack.map(m => m.type).lastIndexOf(instanceId);
  }

  if (index !== -1) {
    modalStack.splice(index, 1);
  }

  // Clear saved path if all modals are closed
  if (modalStack.length === 0) {
    savedPagePath = null;
    console.log('✅ All modals closed, cleared saved path');
  }

  console.log(
    'Modal popped:', instanceId,
    'Stack:', modalStack.map(m => m.instanceId),
    'popHistory:', popBrowserHistory
  );

  // If closed via UI (not browser back), remove the stale browser history entry.
  // ✅ نُعلِم بقية مستمعي popstate (في أي مودال آخر أسفل هذا المودال في المكدّس،
  // مثل EventsModal تحت SinglePostModal) أن الـ history.back() هذا "اصطناعي" —
  // نتيجة إغلاق عبر الواجهة، وليس ضغطة رجوع حقيقية من المستخدم. بدون هذا،
  // يُحذف هذا المودال من modalStack فوراً (بشكل متزامن) قبل وصول حدث popstate
  // الفعلي (غير متزامن) — فحين يصل، يصبح المودال الذي تحته "الأعلى" في المكدّس
  // بالخطأ، ويظن مستمعه أن الرجوع يخصّه فيُغلق نفسه أيضاً رغم أنه لم يُطلب ذلك.
  if (popBrowserHistory) {
    beginBackNavigation();
    try {
      window.history.back();
    } catch (e) {
      // ignore
    } finally {
      // نُبقي العلم مرفوعاً حتى بعد وصول popstate الاصطناعي لهذا التيك، ثم نخفضه
      setTimeout(() => endBackNavigation(), 0);
    }
  }
};

/** Get a copy of the current modal stack (array of { type, instanceId }) */
export const getModalStack = () => [...modalStack];

export const getNextModalZIndex = (base = 1000, offset = 10) => {
  return Math.max(base, 1000000) + (modalStack.length + 1) * Math.max(offset, 100);
};

/**
 * Get the instanceId of the top modal in the stack.
 * Components should compare against this when handling popstate.
 */
export const getTopModal = () =>
  modalStack.length > 0 ? modalStack[modalStack.length - 1].instanceId : null;

/**
 * Get the type of the top modal (useful when you only care about the type).
 */
export const getTopModalType = () =>
  modalStack.length > 0 ? modalStack[modalStack.length - 1].type : null;

/**
 * Check if ANY instance of a modal type is currently active.
 */
export const isModalActive = (modalType) =>
  modalStack.some(m => m.type === modalType);

/**
 * Check if a specific instance is still in the stack.
 */
export const isInstanceActive = (instanceId) =>
  modalStack.some(m => m.instanceId === instanceId);

/** Check if we're handling back navigation */
export const isBackNavigation = () => isHandlingBack;

export const beginBackNavigation = () => {
  isHandlingBack = true;
};

export const endBackNavigation = () => {
  isHandlingBack = false;
};

/** Set back navigation flag */
export const setBackNavigation = (value) => {
  isHandlingBack = value;
};

/**
 * Clear all modal history (used when navigating away via notification)
 */
export const clearModalHistory = () => {
  const stackLength = modalStack.length;
  modalStack = [];

  if (stackLength > 0) {
    window.history.go(-stackLength);
  }
  console.log('Modal history cleared');
};

/**
 * Reset modal stack without affecting browser history
 */
export const resetModalStack = () => {
  modalStack = [];
  savedPagePath = null;
  console.log('Modal stack reset');
};

/** Get the saved page path (before modals were opened) */
export const getSavedPagePath = () => savedPagePath;

/**
 * Handle back navigation – returns the instanceId that should close, or null.
 */
export const handleBackNavigation = () => {
  return modalStack.length > 0 ? modalStack[modalStack.length - 1].instanceId : null;
};

/**
 * Navigate away from modals (for notification clicks).
 */
export const navigateFromModal = (navigateFn, destination) => {
  modalStack = [];
  window.history.replaceState(null, '');
  navigateFn(destination, { replace: true });
  console.log('Navigated from modal to:', destination);
};
